import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { News } from '../types'
import { newsApi } from '../lib/api'
import NewsImage from './NewsImage'
import { useImageCache } from '../hooks/useImageCache'

interface NewsCarouselProps {
  className?: string
}

const NewsCarousel = ({ className = '' }: NewsCarouselProps) => {
  const navigate = useNavigate()
  const { preloadImages } = useImageCache()
  const [news, setNews] = useState<News[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [carouselVisible, setCarouselVisible] = useState(true)
  const autoPlayRef = useRef<number | null>(null)
  const touchStartX = useRef<number>(0)
  const touchEndX = useRef<number>(0)

  useEffect(() => {
    // Verificar si el endpoint de news fue deshabilitado previamente (por 404)
    const newsDisabled = (window as any).__newsEndpointDisabled
    
    // Solo intentar cargar noticias si el endpoint está disponible
    // Si estamos en producción y el endpoint no existe, simplemente no cargar
    const shouldLoadNews = import.meta.env.VITE_ENABLE_NEWS !== 'false' && !newsDisabled
    
    if (shouldLoadNews) {
      loadNews()
    } else {
      setLoading(false)
      setCarouselVisible(false)
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current)
      }
    }
  }, [])

  // Pre-cargar todas las imágenes cuando se cargan las noticias
  useEffect(() => {
    if (news.length > 0) {
      const imageKeys = news.map(n => n.image_url ?? null).filter((key): key is string | null => key !== undefined)
      preloadImages(imageKeys)
    }
  }, [news, preloadImages])

  const loadNews = async () => {
    try {
      const response = await newsApi.getNews(10)
      if (response.success) {
        setNews(response.data || [])
        setCarouselVisible(response.carouselVisible !== false)
      } else {
        setCarouselVisible(false)
      }
    } catch (error: any) {
      // Si el endpoint no existe (404), simplemente ocultar el carousel sin error
      // No loguear errores 404 en ningún entorno para evitar ruido en consola
      if (error.response?.status === 404 || error.name === 'Silent404Error') {
        setCarouselVisible(false)
        // Deshabilitar intentos futuros estableciendo la variable de entorno implícitamente
        if (import.meta.env.PROD) {
          // En producción, no intentar cargar noticias si el endpoint no existe
          ;(window as any).__newsEndpointDisabled = true
        }
      } else {
        // Solo loguear otros errores en desarrollo
        if (import.meta.env.DEV) {
          console.error('Error loading news:', error)
        }
        setCarouselVisible(false)
      }
    } finally {
      setLoading(false)
    }
  }

  // Auto-play carousel
  useEffect(() => {
    if (news.length > 1 && carouselVisible) {
      autoPlayRef.current = window.setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % news.length)
      }, 5000) // Cambiar cada 5 segundos

      return () => {
        if (autoPlayRef.current) {
          window.clearInterval(autoPlayRef.current)
        }
      }
    }
  }, [news.length, carouselVisible])

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
    // Reiniciar auto-play
    if (autoPlayRef.current) {
      window.clearInterval(autoPlayRef.current)
    }
    autoPlayRef.current = window.setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % news.length)
    }, 5000)
  }

  const goToPrevious = () => {
    goToSlide((currentIndex - 1 + news.length) % news.length)
  }

  const goToNext = () => {
    goToSlide((currentIndex + 1) % news.length)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return
    const distance = touchStartX.current - touchEndX.current
    const minSwipeDistance = 50

    if (distance > minSwipeDistance) {
      goToNext()
    } else if (distance < -minSwipeDistance) {
      goToPrevious()
    }
  }


  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl h-64 sm:h-80 animate-pulse"></div>
      </div>
    )
  }

  if (!carouselVisible || news.length === 0) {
    return null
  }

  const currentNews = news[currentIndex]

  return (
    <div className={`relative w-full ${className}`}>
      {/* Carousel Container */}
      <div
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-50 to-white shadow-sm border border-gray-100/50"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* News Card */}
        <div className="relative h-64 sm:h-80 md:h-96">
          {/* Image Background */}
          {currentNews.image_url && (
            <div className="absolute inset-0">
              <NewsImage
                imageKey={currentNews.image_url}
                className="w-full h-full object-cover"
                alt={currentNews.title}
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
            </div>
          )}

          {/* Content */}
          <div className="relative h-full flex flex-col justify-end p-6 sm:p-8 md:p-10">
            <div className="max-w-3xl">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight drop-shadow-lg">
                {currentNews.title}
              </h3>
              <p className="text-white/90 text-sm sm:text-base md:text-lg mb-4 sm:mb-6 line-clamp-2 sm:line-clamp-3 drop-shadow-md">
                {currentNews.content}
              </p>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => {
                    const currentNews = news[currentIndex]
                    if (currentNews) {
                      navigate(`/news?id=${currentNews.id}`)
                    }
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-white/95 backdrop-blur-md text-gray-900 rounded-xl font-semibold text-sm sm:text-base hover:bg-white transition-all duration-200 active:scale-95 shadow-lg"
                >
                  Ver más
                </button>
                {currentNews.link_url && (
                  <a
                    href={currentNews.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-primary-600/95 backdrop-blur-md text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-primary-700 transition-all duration-200 active:scale-95 shadow-lg"
                  >
                    {currentNews.link_text || 'Enlace externo'}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          {news.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200 active:scale-95 z-10"
                aria-label="Noticia anterior"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200 active:scale-95 z-10"
                aria-label="Siguiente noticia"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900" />
              </button>
            </>
          )}
        </div>

        {/* Dots Indicator */}
        {news.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {news.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Ir a noticia ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default NewsCarousel

